import * as SettingsStore from './settingsStore';
import * as RestricitonStore from './restrictionsStore'
import * as WidgetSettingsStore from './WidgetSettingsStore';
import { takeEvery, select, put, call, take, race, all, fork } from "redux-saga/effects";
import { ApplicationState } from '../../store';
import { LoginState } from '../../login/loginStore';
import * as settingsApi from '../../api/settingsApi';
import { UserWithSettingsModel, CompanyWithSettingsModel } from '../models/SettingsModels';
import { RestrictedCompanyModel } from '../../common/models/CommonModels';
import * as companyApi from '../../api/companyApi'
import * as accountApi from '../../api/accountApi'
import * as Notifications from '../../notifications/notificationsStore'
import { localizations } from '../../localization/localization';

export function* watchSettingsSagas() {

    // Settings sagas
    yield takeEvery(SettingsStore.ActionType.RequestPersonalData, fetchPersonalData);
    yield takeEvery(SettingsStore.ActionType.RequestCompanyData, fetchCompanyData);
    yield takeEvery(SettingsStore.ActionType.ToggleEmailVisibilty, togglePersonalEmailVisibilty);
    yield takeEvery(SettingsStore.ActionType.TogglePhoneNumberVisibility, togglePersonalPhoneVisibility);
    yield takeEvery(SettingsStore.ActionType.EditUser, editUserFlow);
    yield takeEvery(SettingsStore.ActionType.EditCompany, editCompanyFlow);
    yield takeEvery(SettingsStore.ActionType.DeleteUser, deleteUserFlow);
    yield takeEvery(SettingsStore.ActionType.AddPhoneNumber, addPhoneNumberFlow);
    yield takeEvery(SettingsStore.ActionType.AddEmail, addEmailFlow);
    yield takeEvery(SettingsStore.ActionType.DeletePhoneNumber, deletePhoneNumberFlow);
    yield takeEvery(SettingsStore.ActionType.DeleteEmail, deleteEmailFlow);

    // RestricitonSagas
    yield takeEvery(RestricitonStore.ActionType.RequestRestrictions, fetchRestrictions);
    yield takeEvery(RestricitonStore.ActionType.AddRestriction, addRestrictionFlow);
    yield takeEvery(RestricitonStore.ActionType.DeleteRestriction, deleteRestrictionFlow);

    // Widget settings sagas
    yield takeEvery(WidgetSettingsStore.ActionType.RequestGenerateApiKey, generateApiKey);
    yield takeEvery(WidgetSettingsStore.ActionType.RequestTestPartnerApiKey, getTestPartnerApiKey);
}

const getUser = (state: ApplicationState) => state.login;

function parseJwt(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    return JSON.parse(window.atob(base64));
};

function* fetchPersonalData() {
    const userData: LoginState = yield select(getUser);

    if (userData && userData.userId) {
        const user = yield call(settingsApi.getUserData, userData.userId)

        yield put(SettingsStore.actionsCreators.receivedPersonalData(user));
    }
}

function* fetchCompanyData() {
    const userData: LoginState = yield select(getUser);

    if (userData && userData.companyId) {
        const company = yield call(settingsApi.getCompanyData, userData.companyId);

        yield put(SettingsStore.actionsCreators.receivedCompanyData(company));
    } else {
        yield put(SettingsStore.actionsCreators.receivedCompanyData(undefined));
    }
}

function* togglePersonalEmailVisibilty(action: SettingsStore.ToggleEmailVisibiltyAction) {

    const user: LoginState = yield select(getUser);

    if (user && user.userId) {
        yield call(settingsApi.setPersonalEmailVisibility, user.userId, action.visible);
    }
}

function* togglePersonalPhoneVisibility(action: SettingsStore.TogglePhoneNumberVisibilityAction) {
    const user: LoginState = yield select(getUser);

    if (user && user.userId) {
        yield call(settingsApi.setPersonalPhoneVisibility, user.userId, action.visible);
    }
}

function* editUserFlow(action: SettingsStore.EditUserAction) {
    const response: UserWithSettingsModel = yield call(settingsApi.editUser, action.user);

    if (response) {
        yield put(SettingsStore.actionsCreators.receivedPersonalData(action.user));
    }
}

function* editCompanyFlow(action: SettingsStore.EditCompanyAction) {
    const response: CompanyWithSettingsModel = yield call(settingsApi.editCompany, action.company);

    if (response) {
        yield put(SettingsStore.actionsCreators.receivedCompanyData(response));
    }
}

function* fetchRestrictions() {
    const res: RestrictedCompanyModel[] = yield call(companyApi.getRestrictedCompanies, "");
    yield put(RestricitonStore.actionCreators.receivedRestrictions(res));
}

function* addRestrictionFlow() {

    // Will inform UI to shoe add dialog.,
    yield put(RestricitonStore.actionCreators.addStarted());

    // Ok or cancel
    const { submit } = yield race({
        submit: take(RestricitonStore.ActionType.AddSubmitted),
        cancel: take(RestricitonStore.ActionType.AddCancelled)
    });

    if (submit) {
        const selected: string[] = submit.selected;

        // Use api call for single add for all added restrictions.
        yield all(selected.map(id => addSingleRestriction(id)));
    }

    // Will hide the add dialog.
    yield put(RestricitonStore.actionCreators.addSuccess())

    // Reload restrictions.
    yield fetchRestrictions();
}

function* addSingleRestriction(id: string): IterableIterator<any> {
    yield companyApi.addVisibilityRestriction(id);
}

function* deleteRestrictionFlow(action: RestricitonStore.DeleteRestrictionAction) {

    // Delete visibility restriction.
    yield call(companyApi.deleteRestriction, action.id);

    // Reload restrictions.
    yield fetchRestrictions();
}

function* deleteUserFlow(action: SettingsStore.DeleteUserAction) {
    const user = action.user;

    const success = yield call(accountApi.deleteUser, user.id);
    if (success) {
        yield put(SettingsStore.actionsCreators.requestCompanyData());
    }
}

function* contactAddSuccess(message: string) {
    yield put(Notifications.actionCreators.addNotification({ id: "contactAddSuccess", message, variant: "success", autohide: true }));
}

function* contactAddError() {
    yield put(Notifications.actionCreators.addNotification({ id: "contactAddError", message:  localizations.store.contactAddError, variant: "error", autohide: true  }));
}

function* addPhoneNumberFlow(action: SettingsStore.AddOrEditPhoneNumberAction) {
    const user = yield select(getUser);

    const phoneNumberDto = Object.assign({}, action.data, { companyId: user.companyId });

    const result = yield call(settingsApi.addCompanyPhoneNumber, phoneNumberDto);

    if (result) {
        if (action.data.id && action.data.id.length > 0) {
            yield fork(contactAddSuccess, localizations.store.contactAddSuccess);
        } else {
            yield fork(contactAddSuccess, localizations.common.phoneNumber + " " + action.data.phoneNumber + " " + localizations.actions.addedLowercase);
        }
        yield put(SettingsStore.actionsCreators.receivedCompanyData(result));
    } else {
        yield fork(contactAddError);
    }
}

function* addEmailFlow(action: SettingsStore.AddOrEditEmailAction) {
    const user = yield select(getUser);

    const emailDto = Object.assign({}, action.data, { companyId: user.companyId });

    const result = yield call(settingsApi.addCompanyEmail, emailDto);

    if (result) {
        if (action.data.id && action.data.id.length > 0) {
            yield fork(contactAddSuccess, localizations.store.addEmailSuccess);
        } else {
            yield fork(contactAddSuccess, localizations.common.email + " " + action.data.email + " " + localizations.actions.addedLowercase);
        }

        yield put(SettingsStore.actionsCreators.receivedCompanyData(result));
    } else {
        yield fork(contactAddError);
    }
}

function* deletePhoneNumberFlow(action: SettingsStore.DeletePhoneNumberAction) {
    const user = yield select(getUser);

    const company = yield call(settingsApi.deletePhoneNumber, { contactId: action.data.id, companyId: user.companyId });

    if (company) {
        yield put(SettingsStore.actionsCreators.receivedCompanyData(company));
    }
}

function* deleteEmailFlow(action: SettingsStore.DeleteEmailAction) {
    const user = yield select(getUser);

    const company = yield call(settingsApi.deleteEmail, { contactId: action.data.id, companyId: user.companyId });

    if (company) {
        yield put(SettingsStore.actionsCreators.receivedCompanyData(company));
    }
}

function* generateApiKey(action: WidgetSettingsStore.RequestGenerateApiKeyAction) {
    const userData: LoginState = yield select(getUser);

    if (userData && userData.companyId) {
        const companyApiKey = yield call(settingsApi.generateApiKey, userData.companyId);

        yield put(WidgetSettingsStore.actionCreators.ReceivedGenerateApiKey(companyApiKey));
    } else {
        yield put(WidgetSettingsStore.actionCreators.ReceivedGenerateApiKey(undefined));
    }
}

function* getTestPartnerApiKey(action: WidgetSettingsStore.RequestTestPartnerApiKeyAction){
    const testPartnerApiKey = yield call(settingsApi.getTestPartnerApiKey);
    yield put(WidgetSettingsStore.actionCreators.ReceivedTestPartnerApiKey(testPartnerApiKey));
}


// WEBPACK FOOTER //
// ./src/account/store/sagas.ts